import {orderedLocations} from 'common/flight-leg/route-visits';
import React, {Dispatch, ReducerAction, useEffect, useRef, useState} from "react";
import {Reducer, State} from './state';
import {Alert, Button, Col, Icon, Row, Spin} from "antd";
import {DraggableBodyRow} from "../../../TableDragSorting";
import {ColumnProps} from "antd/lib/table";
import {Location} from "../../../../common/types/location";
import CenteredLoadingScreen from "../../../CenteredLoadingScreen";
import useRouteEditorApi from "./hook";
import "./route-editor.less";
import {LocationSelect} from "../../../form/select/LocationSelect";
import useApolloClientHelper from "../../../../hooks/useApolloClientHelper";
import ETable from "../../../enchanced-antd-table";

export interface RouteEditorProps {
    initFlightPath?: string[],
    stale?: boolean,
    disabled?: boolean,
    onReload?: () => void,
    overrideState?: {
        state: State,
        dispatch: Dispatch<ReducerAction<typeof Reducer>>
    }
}

/**
 * Asynchronously turns a list of location IDs into location objects of the Location type
 * @param locIds list of location IDs
 */
function useDereferencedLocations(locIds: string[]){
    const apolloHelper = useApolloClientHelper();
    const [ locationList, setLocationList ] = useState<Location[]>(null);
    const [ loading, setLoading ] = useState(false);
    const [lookupError, setLookupError] = useState('');

    const helperRef = useRef(apolloHelper);
    helperRef.current = apolloHelper;
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setLookupError('');
        (async () => {
            try {
                const locations = await helperRef.current.resolveEntityIdsByType(Array.from(new Set(locIds)), 'Location');
                if (!cancelled) setLocationList(orderedLocations(locIds, locations));
            } catch (err) {
                if (!cancelled) {
                    setLocationList(null);
                    setLookupError(err.message || 'Failed to load location data');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {cancelled = true;};
    }, [locIds]);

    return {
        locationList: locationList,
        loading: loading,
        lookupError
    };
}

const RouteEditor: React.FC<RouteEditorProps> = (props) => {
    const api = useRouteEditorApi({
        initFlightPath: props.overrideState ? undefined : props.initFlightPath,
        overrideState: props.overrideState?.state,
        overrideDispatch: props.overrideState?.dispatch
    });

    const visits = api.state.path.present;
    const ids = React.useMemo(() => visits.map(visit => visit.locationID), [visits]);
    const { locationList, loading, lookupError } = useDereferencedLocations(ids);

    const { state, route } = api;

    let table: React.ReactNode = <CenteredLoadingScreen />

    function renderLocName(loc: Location){
        return loc.name + (loc.field && loc.block ? " (" + loc.block + "-" + loc.field + ")" : "");
    }

    if (locationList !== null) {
        const columns: ColumnProps<Location>[] = [
            {
                title: '',
                key: 'delete',
                render: (_, __, index) => {
                    return (
                        <Button
                            disabled={props.disabled}
                            size="small"
                            shape="circle"
                            type="danger"
                            icon="delete"
                            onClick={() => route.remove(index)}
                        />
                    )
                },
                width: 50
            },
            {
                title: '#',
                key: '#',
                render: (_, __, index) => index + 1,
                width: 25
            },
            {
                title: "Name",
                width: 400,
                render: (_, loc) => (
                    renderLocName(loc)
                )
            },
            {
                title: 'Type',
                key: 'type',
                dataIndex: 'type'
            },
            {
                title: '',
                render: (_, loc, index) => (
                    <span className="mc-route-editor-actions">
                        <Button
                            disabled={props.disabled}
                            title="Duplicate"
                            size="small"
                            icon="copy"
                            onClick={() => {
                                route.add(loc._id, index + 1);
                            }}
                        />
                    </span>
                )
            }
        ]

        table = (
            <ETable<Location>
                className="draggable-table mc-table mc-table-bordered"
                dataSource={locationList}
                columns={columns}
                rowKey={(_, index) => state.path.present[index]?.key}
                size="small"
                pagination={false}
                components={{
                    body: {
                        row: DraggableBodyRow
                    }
                }}
                onRow={(row, index) => {
                    const rowProps = {
                        moveRow: route.move,
                        canDrag: !props.disabled,
                        canDrop: !props.disabled,
                        index
                    }
                    return { ...rowProps}
                }}
            />
        )
    }

    return (
        <div className="mc-route-editor">
            {props.stale && <Alert type="warning" showIcon message="The flight route or departure changed while you were editing." description={<Button disabled={props.disabled} onClick={props.onReload}>Reload current route</Button>} style={{marginBottom: 16}} />}

            <Row type="flex" gutter={6} style={{ marginBottom: 12, alignItems: 'center' }}>
                <Col
                    style={{ flex: 1 }}
                >
                    <h4 style={{ margin: 0 }}>Drag to reorder flight locations</h4>
                </Col>
                <Col>
                    <span style={{marginRight: '12px'}}>Add location:</span>
                    <LocationSelect
                        disabled={props.disabled}
                        onChange={locID => api.route.add(locID)}
                        style={{minWidth: '12rem'}}
                    />
                </Col>
                <Col>
                    <Button
                        icon="undo"
                        disabled={props.disabled || !state.path.past.length}
                        onClick={route.undo}
                        title="Undo"
                    />
                </Col>
                <Col>
                    <Button
                        disabled={props.disabled || !state.path.future.length}
                        icon="redo"
                        onClick={route.redo}
                        title="Redo"
                    />
                </Col>
            </Row>
            {lookupError && <Alert type="error" showIcon message={lookupError} style={{marginBottom: 16}} />}
            <Spin spinning={loading} indicator={<Icon type="loading" />}>
                {lookupError ? null : table}
            </Spin>
        </div>
    )
}

export default RouteEditor;